import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import { logException } from '@atlaskit/editor-common/monitoring';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import { hasSnippetMediaNodes } from '../pm-plugins/snippetMedia';
import type {
	InsertSnippetOptions,
	InsertSnippet,
	SnippetsPlugin,
	SnippetsPluginOptions,
} from '../snippetsPluginType';

interface InsertSnippetParams extends InsertSnippetOptions {
	api: Parameters<SnippetsPlugin>[0]['api'];
	config: SnippetsPluginOptions;
	tr: Transaction;
}

function insertSnippetContent({
	tr,
	api,
	config,
	inputMethod = INPUT_METHOD.QUICK_INSERT,
	snippet,
}: InsertSnippetParams): Transaction | null {
	const fireAnalyticsEvent = api?.analytics?.actions?.fireAnalyticsEvent;
	const { snippetId, templateType } = snippet;
	const adf = snippet.body.content?.filter((node) => node !== undefined);

	if (!adf || adf.length === 0) {
		fireAnalyticsEvent?.({
			action: ACTION.FAILED_TO_INSERT,
			actionSubject: ACTION_SUBJECT.DOCUMENT,
			actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
			attributes: { reason: 'emptyBody', snippetId, templateType },
			eventType: EVENT_TYPE.OPERATIONAL,
		});
		logException(new Error('Empty snippet body'), {
			location: 'editor-plugin-snippets/insertSnippetContent',
		});
		return null;
	}

	const hasMedia = hasSnippetMediaNodes(adf);
	let result: Transaction;
	try {
		const fragment = Fragment.fromJSON(tr.doc.type.schema, adf);
		const insertPos = tr.selection.head;
		result = tr.replaceWith(insertPos, insertPos, fragment);
	} catch (error) {
		fireAnalyticsEvent?.({
			action: ACTION.FAILED_TO_INSERT,
			actionSubject: ACTION_SUBJECT.DOCUMENT,
			actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
			attributes: { reason: 'parseError', snippetId, templateType },
			eventType: EVENT_TYPE.OPERATIONAL,
		});
		logException(error as Error, {
			location: 'editor-plugin-snippets/insertSnippetContent',
		});
		return null;
	}

	fireAnalyticsEvent?.({
		action: ACTION.INSERTED,
		actionSubject: ACTION_SUBJECT.DOCUMENT,
		actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
		attributes: { ...(hasMedia && { hadMedia: true }), inputMethod, snippetId, templateType },
		eventType: EVENT_TYPE.TRACK,
	});
	try {
		config.onSnippetInserted?.({ inputMethod, snippet });
	} catch (error) {
		logException(error as Error, {
			location: 'editor-plugin-snippets/insertSnippetContent/onSnippetInserted',
		});
	}
	return result;
}

export const insertSnippet =
	(api: Parameters<SnippetsPlugin>[0]['api'], config: SnippetsPluginOptions): InsertSnippet =>
	(options) =>
	({ tr }) => {
		if (!isExperimentEnabled('platform_editor_snippets_plugin_confluence')) {
			return null;
		}

		return insertSnippetContent({ ...options, api, config, tr });
	};
